import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, ClipboardList, LayoutGrid, List, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCorners,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const COLUMNS = [
  { id: "pending_review", label: "Pending Review", color: "#D97706", bg: "#FEF3C7" },
  { id: "in_progress",    label: "In Progress",    color: "#0284C7", bg: "#DBEAFE" },
  { id: "on_hold",        label: "On Hold",         color: "#7C3AED", bg: "#F3E8FF" },
  { id: "completed",      label: "Completed",       color: "#059669", bg: "#D1FAE5" },
  { id: "cancelled",      label: "Cancelled",       color: "#DC2626", bg: "#FEE2E2" },
];

const STATUS_OPTIONS = ["", "pending_review", "in_progress", "on_hold", "completed", "cancelled"];
const PRIORITY_OPTIONS = ["", "urgent", "high", "medium", "low"];

const statusColors = {
  pending_review: { bg: "#FEF3C7", text: "#D97706" },
  in_progress:    { bg: "#DBEAFE", text: "#1D4ED8" },
  completed:      { bg: "#D1FAE5", text: "#059669" },
  cancelled:      { bg: "#FEE2E2", text: "#DC2626" },
  on_hold:        { bg: "#F3E8FF", text: "#7C3AED" },
};
const priorityColors = {
  urgent: { bg: "#FEE2E2", text: "#DC2626" },
  high:   { bg: "#FFEDD5", text: "#EA580C" },
  medium: { bg: "#FEF3C7", text: "#D97706" },
  low:    { bg: "#F0FDF4", text: "#16A34A" },
};
const fmt = (s) => s?.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ") || "";


// ─── Kanban Card ───────────────────────────────────────────────────────────
function KanbanCard({ req, onDeleteClick }) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging: isSortDragging } =
    useSortable({ id: req.id, data: { status: req.status } });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortDragging ? 0.4 : 1,
  };
  const pc = req.priority ? priorityColors[req.priority] : null;
  const isOverdue = req.due_date && req.due_date < new Date().toISOString().slice(0, 10) && !["completed", "cancelled"].includes(req.status);

  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-card-${req.id}`}
      className="bg-white rounded-lg border p-3 cursor-pointer hover:shadow-md transition-shadow select-none group relative"
      style={{ ...style, borderColor: isOverdue ? "#FCA5A5" : "#E7E5E4", cursor: isSortDragging ? "grabbing" : "pointer" }}
    >
      <div {...attributes} {...listeners} onClick={() => navigate(`/requests/${req.id}`)}>
        <p className="text-sm font-medium leading-snug mb-2 pr-5" style={{ color: "#292524" }}>{req.title}</p>
        <p className="text-xs mb-2" style={{ color: "#A8A29E" }}>{req.client_name}</p>
        <div className="flex items-center justify-between flex-wrap gap-1">
          {pc ? (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: pc.bg, color: pc.text }}>{fmt(req.priority)}</span>
          ) : <span />}
          <div className="flex items-center gap-2">
            {req.due_date && (
              <span className="text-xs" style={{ color: isOverdue ? "#DC2626" : "#A8A29E" }}>
                {isOverdue ? "Overdue" : req.due_date}
              </span>
            )}
            {req.time_estimate_hours && (
              <span className="text-xs" style={{ color: "#A8A29E" }}>{req.time_estimate_hours}h</span>
            )}
          </div>
        </div>
      </div>
      <button
        data-testid={`kanban-delete-${req.id}`}
        onClick={(e) => { e.stopPropagation(); onDeleteClick(req); }}
        className="absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
        style={{ color: "#DC2626" }}
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}

// ─── Kanban Column ─────────────────────────────────────────────────────────
function KanbanColumn({ col, cards, isOver, onDeleteClick }) {
  const { setNodeRef } = useSortable({ id: col.id });
  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-col-${col.id}`}
      className="flex flex-col rounded-xl border overflow-hidden min-w-56 w-64 flex-shrink-0"
      style={{ borderColor: isOver ? col.color : "#E7E5E4", background: "#FAFAF9" }}
    >
      <div className="px-3 py-2.5 flex items-center justify-between border-b" style={{ borderColor: "#E7E5E4" }}>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: col.color }} />
          <span className="text-xs font-semibold" style={{ color: "#292524" }}>{col.label}</span>
        </div>
        <span className="text-xs px-1.5 py-0.5 rounded-full font-medium" style={{ background: col.bg, color: col.color }}>{cards.length}</span>
      </div>
      <SortableContext items={cards.map((c) => c.id)} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-2 p-2 min-h-20">
          {cards.map((req) => <KanbanCard key={req.id} req={req} onDeleteClick={onDeleteClick} />)}
        </div>
      </SortableContext>
    </div>
  );
}


// ─── Main Component ────────────────────────────────────────────────────────
export default function Requests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [viewMode, setViewMode] = useState("board"); // "board" | "list"
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", client_id: "", billing_type: "retained" });
  const [saving, setSaving] = useState(false);
  const [activeReq, setActiveReq] = useState(null);
  const [overColId, setOverColId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, title }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  useEffect(() => {
    Promise.all([api.get("/requests"), api.get("/clients")])
      .then(([r, c]) => { setRequests(r.data); setClients(c.data); })
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/requests/${deleteTarget.id}`);
      setRequests((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      toast.success("Task deleted");
    } catch {
      toast.error("Failed to delete task");
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleCreate = async (e) => {    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/requests", form);
      setRequests((prev) => [r.data, ...prev]);
      setShowDialog(false);
      setForm({ title: "", description: "", client_id: "", billing_type: "retained" });
      toast.success("Request created");
      navigate(`/requests/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create request");
    } finally {
      setSaving(false);
    }
  };

  const filtered = requests.filter((r) => {
    const matchSearch = r.title.toLowerCase().includes(search.toLowerCase()) || r.client_name?.toLowerCase().includes(search.toLowerCase());
    const matchClient = !filterClient || r.client_id === filterClient;
    const matchStatus = !filterStatus || r.status === filterStatus;
    const matchPriority = !filterPriority || r.priority === filterPriority;
    return matchSearch && matchClient && matchStatus && matchPriority;
  });

  // ── Drag-and-drop handlers ────────────────────────────────────────────────
  const handleDragStart = ({ active }) => {
    setActiveReq(requests.find((r) => r.id === active.id) || null);
  };

  const handleDragOver = ({ over }) => {
    if (!over) { setOverColId(null); return; }
    // Check if hovering over a column id directly
    const colId = COLUMNS.find((c) => c.id === over.id)?.id
      || requests.find((r) => r.id === over.id)?.status;
    setOverColId(colId || null);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveReq(null);
    setOverColId(null);
    if (!over || !active) return;
    const draggedReq = requests.find((r) => r.id === active.id);
    if (!draggedReq) return;
    // Determine target column
    let targetStatus = COLUMNS.find((c) => c.id === over.id)?.id
      || requests.find((r) => r.id === over.id)?.status;
    if (!targetStatus || targetStatus === draggedReq.status) return;

    // Optimistic update
    setRequests((prev) => prev.map((r) => r.id === draggedReq.id ? { ...r, status: targetStatus } : r));
    try {
      await api.put(`/requests/${draggedReq.id}`, { status: targetStatus });
    } catch {
      // Roll back
      setRequests((prev) => prev.map((r) => r.id === draggedReq.id ? { ...r, status: draggedReq.status } : r));
      toast.error("Failed to update status");
    }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="requests-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif text-3xl font-bold" style={{ color: "#292524" }}>Tasks</h1>
            <p className="text-sm mt-0.5" style={{ color: "#57534E" }}>{filtered.length} task{filtered.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* View toggle */}
            <div className="flex items-center rounded-lg border overflow-hidden" style={{ borderColor: "#E7E5E4" }}>
              <button
                data-testid="board-view-btn"
                onClick={() => setViewMode("board")}
                className="p-2 transition-colors"
                style={{ background: viewMode === "board" ? "#F5F3EF" : "#fff", color: viewMode === "board" ? "#292524" : "#A8A29E" }}
              >
                <LayoutGrid size={15} />
              </button>
              <button
                data-testid="list-view-btn"
                onClick={() => setViewMode("list")}
                className="p-2 transition-colors"
                style={{ background: viewMode === "list" ? "#F5F3EF" : "#fff", color: viewMode === "list" ? "#292524" : "#A8A29E" }}
              >
                <List size={15} />
              </button>
            </div>
            <Button
              data-testid="create-request-button"
              onClick={() => setShowDialog(true)}
              className="flex items-center gap-2"
              style={{ background: "#292524", color: "#fff" }}
            >
              <Plus size={15} strokeWidth={1.5} />
              New Task
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#A8A29E" }} />
            <input
              data-testid="request-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tasks..."
              className="w-full pl-9 pr-4 py-2 rounded-lg border text-sm outline-none"
              style={{ borderColor: "#E7E5E4", background: "#fff", color: "#292524" }}
            />
          </div>
          <select data-testid="filter-client-select" value={filterClient} onChange={(e) => setFilterClient(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm outline-none" style={{ borderColor: "#E7E5E4", background: "#fff", color: "#292524" }}>
            <option value="">All Clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="filter-status-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm outline-none" style={{ borderColor: "#E7E5E4", background: "#fff", color: "#292524" }}>
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.filter(Boolean).map((s) => <option key={s} value={s}>{fmt(s)}</option>)}
          </select>
          <select data-testid="filter-priority-select" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm outline-none" style={{ borderColor: "#E7E5E4", background: "#fff", color: "#292524" }}>
            <option value="">All Priorities</option>
            {PRIORITY_OPTIONS.filter(Boolean).map((p) => <option key={p} value={p}>{fmt(p)}</option>)}
          </select>
        </div>

        {/* Board View */}
        {viewMode === "board" && (
          <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
            <div className="flex gap-4 overflow-x-auto pb-4">
              {COLUMNS.map((col) => (
                <KanbanColumn
                  key={col.id}
                  col={col}
                  cards={filtered.filter((r) => r.status === col.id)}
                  isOver={overColId === col.id}
                  onDeleteClick={setDeleteTarget}
                />
              ))}
            </div>
            <DragOverlay>
              {activeReq && (
                <div className="bg-white rounded-lg border p-3 shadow-xl w-64" style={{ borderColor: "#E7E5E4" }}>
                  <p className="text-sm font-medium" style={{ color: "#292524" }}>{activeReq.title}</p>
                  <p className="text-xs mt-1" style={{ color: "#A8A29E" }}>{activeReq.client_name}</p>
                </div>
              )}
            </DragOverlay>
          </DndContext>
        )}

        {/* List View */}
        {viewMode === "list" && (
          <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: "#E7E5E4" }}>
            {loading ? (
              <div className="py-12 text-center text-sm" style={{ color: "#A8A29E" }}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="py-12 text-center">
                <ClipboardList size={32} className="mx-auto mb-3" style={{ color: "#E7E5E4" }} />
                <p className="text-sm" style={{ color: "#A8A29E" }}>No tasks found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: "1px solid #E7E5E4" }}>
                    <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Task</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Client</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Priority</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Est.</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Date</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((req) => {
                    const sc = statusColors[req.status] || { bg: "#F5F3EF", text: "#57534E" };
                    const pc = req.priority ? priorityColors[req.priority] : null;
                    return (
                      <tr key={req.id} data-testid={`request-row-${req.id}`} onClick={() => navigate(`/requests/${req.id}`)}
                        className="cursor-pointer hover:bg-stone-50 transition-colors border-b" style={{ borderColor: "#F5F3EF" }}>
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium" style={{ color: "#292524" }}>{req.title}</p>
                        </td>
                        <td className="px-4 py-4 text-sm" style={{ color: "#57534E" }}>{req.client_name}</td>
                        <td className="px-4 py-4">
                          <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: sc.bg, color: sc.text }}>{fmt(req.status)}</span>
                        </td>
                        <td className="px-4 py-4">
                          {pc ? <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: pc.bg, color: pc.text }}>{fmt(req.priority)}</span> : <span style={{ color: "#D6D3D1" }}>—</span>}
                        </td>
                        <td className="px-4 py-4 text-sm" style={{ color: "#57534E" }}>{req.time_estimate_hours ? `${req.time_estimate_hours}h` : "—"}</td>
                        <td className="px-4 py-4 text-sm" style={{ color: "#A8A29E" }}>{req.created_at?.slice(0, 10)}</td>
                        <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                          <button
                            data-testid={`list-delete-${req.id}`}
                            onClick={() => setDeleteTarget(req)}
                            className="p-1.5 rounded hover:bg-red-50 transition-colors"
                            style={{ color: "#DC2626" }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>        <DialogContent data-testid="create-request-dialog">
          <DialogHeader><DialogTitle className="font-serif">New Task</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <Label>Client</Label>
              <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E7E5E4", background: "#fff" }}>
                <option value="">No client (internal)</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><Label>Title *</Label><Input data-testid="new-request-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>Description</Label><Textarea data-testid="new-request-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="min-h-20 resize-none" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} style={{ background: "#292524", color: "#fff" }}>{saving ? "Creating..." : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-task-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>"{deleteTarget?.title}"</strong>? This will also remove all messages, time logs, and stages. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-task-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-task-confirm"
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
